// Public (unauthenticated) API calls backing the QR-scan pages
import { consumeLiveDemoSid } from '../utils/liveDemoSid';
import { getOptionalAuthHeaders } from './authService';
import type { PublicBrand } from '../components/shopper/Brand';

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || '';

export interface ProductImage {
  s3Key: string;
  url: string;
  order: number;
  filename: string;
  contentType: string;
  size: number;
  uploadedAt: string;
}

export interface BackgroundColor {
  r: number;
  g: number;
  b: number;
  x: number;
  y: number;
  prominence: number;
}

export interface Creator {
  userId: string;
  displayName: string;
  verified: boolean;
  /** The brand's logos and colours (#462), when it set them. */
  brand?: PublicBrand;
}

export interface CollectionField {
  name: string;
  sanitizedName?: string;
  type: string;
  isItemField: boolean;
  /** The product's name field (#395): its value is the product's name. */
  isNameField?: boolean;
}

export interface PublicMainProduct {
  versionCount: number;
  blockchainData: any; // null when the chain read failed
}

export interface PublicProduct {
  id: string;
  mainObjectId: string;
  collectionId: string;
  collectionName: string;
  collectionFields: CollectionField[];
  images: ProductImage[];
  description: string;
  creator: Creator | null;
  versionCount: number;
  blockchainData: any;
  createdAt: string;
  updatedAt: string;
  // Item specific fields
  type?: 'main' | 'sub';
  objectId?: string;
  mainProductId?: string; // Database ID of main product (for items)
  // Items: what the page shows of the parent product. Embedded so the page
  // never requests the parent, which would record a second scan (#312).
  mainProduct?: PublicMainProduct;
  // How the QR behaves when scanned; absent ⇒ 'basiqr' (show this page).
  scanMode?: 'basiqr' | 'redirect' | 'choice';
  destinationUrl?: string;
  interstitial?: { countdownSeconds: number };
  // Present only when the caller is an authenticated consumer (Consumer Vault).
  savedByViewer?: boolean;
  // With savedByViewer: whether they saved it as owned or as a wish.
  savedIntent?: 'own' | 'want';
}

export interface VersionData {
  productId: string;
  objectId: string;
  displayVersion: number;
  actualVersion: string;
  totalVersions: number;
  retrievedAt: string;
  data: any;
  notFound?: boolean;
  error?: string;
  message?: string;
}

export interface VersionCountData {
  productId: string;
  totalVersions: number;
  versions: string[];
}

// Image authenticity anchoring (issue #244 Phase 1): per-image SHA-256 vs the on-chain registry
export type ImageVerificationOverall = 'verified' | 'mismatch' | 'partial' | 'unanchored';
export type ImageVerificationStatus = 'verified' | 'mismatch' | 'unanchored' | 'missing-in-s3';

export interface ImageVerificationImage {
  slot: number;
  s3Key: string;
  status: ImageVerificationStatus;
  anchoredHashHex?: string;
  currentHashHex?: string;
  anchoredAtMs?: number; // epoch ms from chain
  version?: number; // on-chain record version (bumps on re-anchor)
  anchorTxDigest?: string; // tx digest recorded at anchor time (may be absent)
}

export interface ImageVerification {
  productId: string;
  productType: 'main' | 'item';
  targetId?: string; // on-chain object id
  source: 'chain';
  overall: ImageVerificationOverall;
  images: ImageVerificationImage[];
}

// Fetch a public product by ID (no auth required)
export const getPublicProduct = async (productId: string): Promise<PublicProduct> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (typeof window !== 'undefined') {
      try {
        // Collect client-side analytics info
        headers['x-timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
        headers['x-screen-resolution'] = `${window.screen.width}x${window.screen.height}`;
      } catch (e) {
        // Ignore errors extracting analytics info to not break product loading
        console.warn('Failed to extract client analytics:', e);
      }
    }

    // Consumer Vault: a signed-in viewer's token lets the backend return
    // `savedByViewer` and skip recording a vault re-open as a scan. Anonymous
    // visitors get `{}` and the request is exactly what it was before.
    Object.assign(headers, await getOptionalAuthHeaders());

    const liveSid = consumeLiveDemoSid();
    const query = liveSid ? `?live=${encodeURIComponent(liveSid)}` : '';

    const response = await fetch(`${API_BASE_URL}api/public/products/${productId}${query}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Product not found');
      }
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch product');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching public product:', error);
    throw error;
  }
};

// Fetch one historical version of a public product
export const getPublicProductVersion = async (
  productId: string,
  versionNumber: number
): Promise<VersionData> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}api/public/products/${productId}/version/${versionNumber}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Product or version not found');
      }
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch version');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching product version:', error);
    throw error;
  }
};

// Fetch the total version count for a public product
export const getPublicVersionCount = async (productId: string): Promise<VersionCountData> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}api/public/products/${productId}/version-count`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch version count');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching version count:', error);
    throw error;
  }
};

// Fetch the on-chain image verification for a public product. Returns null for
// ANY non-200 (503 = feature not deployed, 404, 429, 5xx) or network failure —
// the caller renders no badge in that case; this never throws.
export const getImageVerification = async (
  productId: string
): Promise<ImageVerification | null> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}api/public/products/${productId}/image-verification`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.status !== 200) {
      return null;
    }

    const body = await response.json();
    if (!body || typeof body !== 'object' || !body.data) {
      return null;
    }
    return body.data as ImageVerification;
  } catch (error) {
    console.warn('Image verification unavailable:', error);
    return null;
  }
};

// Build an IOTA explorer URL for an on-chain object
export const getBlockchainExplorerUrl = (
  objectId: string,
  network: 'testnet' | 'prod' = 'testnet'
): string => {
  const networkParam = network === 'prod' ? 'mainnet' : 'testnet';
  return `https://explorer.iota.org/object/${objectId}?network=${networkParam}`;
};

// Build an IOTA explorer URL for a transaction block (sibling of getBlockchainExplorerUrl)
export const getBlockchainExplorerTxUrl = (
  digest: string,
  network: 'testnet' | 'prod' = 'testnet'
): string => {
  const networkParam = network === 'prod' ? 'mainnet' : 'testnet';
  return `https://explorer.iota.org/txblock/${digest}?network=${networkParam}`;
};

// Build the layered CSS radial-gradient background from extracted colors
export const generateGradientCSS = (colors: BackgroundColor[]): string => {
  if (!colors || colors.length === 0) {
    // Fallback gradient
    return `
      radial-gradient(circle at 30% 40%, rgba(0, 180, 216, 0.6) 0%, transparent 60%),
      radial-gradient(circle at 70% 60%, rgba(100, 50, 200, 0.5) 0%, transparent 60%)
    `;
  }

  return colors
    .map((color, index) => {
      const opacity = 0.8 - index * 0.15;
      return `radial-gradient(
        circle at ${color.x}% ${color.y}%,
        rgba(${color.r}, ${color.g}, ${color.b}, ${opacity}) 0%,
        rgba(${color.r}, ${color.g}, ${color.b}, ${opacity * 0.5}) 30%,
        transparent 60%
      )`;
    })
    .join(',\n');
};

